import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../lib/api";
import { apiErr } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Input, Textarea, Label } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import {
  Building2, Map, Zap, Droplets, Truck, Users, ShieldCheck, Target, UserCheck,
  FolderCheck, ClipboardList, Download, Save, Info, ChevronDown, CheckCircle2, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const ICONS = { Building2, Map, Zap, Droplets, Truck, Users, ShieldCheck, Target, UserCheck, FolderCheck };
const BAND = {
  ready: { label: "Ready to onboard", tone: "success", color: "text-[#00E65B]" },
  in_progress: { label: "In progress", tone: "warning", color: "text-[#FFB800]" },
  early: { label: "Getting started", tone: "outline", color: "text-muted-foreground" },
};

function SectionCard({ s, checklist, answers, onCheck, onAnswer, open, onToggle, sectionScore }) {
  const Icon = ICONS[s.icon] || ClipboardList;
  const pct = sectionScore?.pct ?? 0;
  return (
    <div className="card-flat overflow-hidden" data-testid={`readiness-section-${s.key}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-surface-hover transition-colors"
        data-testid={`readiness-toggle-${s.key}`}
      >
        <div className="h-9 w-9 rounded-sm bg-accent/10 border border-accent/20 flex items-center justify-center shrink-0">
          <Icon className="h-4 w-4 text-accent" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display font-semibold text-sm truncate">{s.title}</div>
          <div className="flex items-center gap-2 mt-1">
            <Progress value={pct} className="h-1.5 flex-1 max-w-[160px]" />
            <span className="text-[11px] font-mono text-muted-foreground">{sectionScore?.done ?? 0}/{sectionScore?.total ?? 0}</span>
          </div>
        </div>
        {pct === 100 && <CheckCircle2 className="h-4 w-4 text-[#00E65B] shrink-0" />}
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform shrink-0 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-4 border-t border-border pt-4">
          <div className="rounded-sm bg-surface-hover/50 border border-border p-3 space-y-2">
            <p className="text-xs leading-relaxed text-muted-foreground flex gap-2">
              <Info className="h-3.5 w-3.5 text-accent shrink-0 mt-0.5" />
              <span><span className="font-semibold text-foreground">Why it matters: </span>{s.why}</span>
            </p>
            <p className="text-[11px] text-muted-foreground/80 pl-[22px]"><span className="font-semibold">In the platform: </span>{s.platform}</p>
          </div>

          <div>
            <div className="label-eyebrow text-[10px] mb-2">Checklist — gather these</div>
            <div className="space-y-1.5">
              {s.checklist.map((it) => (
                <label key={it.id} className="flex items-start gap-2.5 p-2 rounded-sm hover:bg-surface-hover cursor-pointer" data-testid={`readiness-check-${it.id}`}>
                  <input
                    type="checkbox"
                    checked={!!checklist[it.id]}
                    onChange={(e) => onCheck(it.id, e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-[hsl(var(--accent))] cursor-pointer"
                    data-testid={`readiness-checkbox-${it.id}`}
                  />
                  <span className="text-sm leading-snug">
                    {it.label}
                    {it.hint && <span className="block text-[11px] text-muted-foreground">{it.hint}</span>}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <div className="label-eyebrow text-[10px] mb-2">Questionnaire</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {s.questions.map((q) => (
                <div key={q.id} className={q.type === "textarea" ? "sm:col-span-2" : ""}>
                  <Label className="text-xs">{q.label}</Label>
                  {q.type === "select" ? (
                    <Select value={answers[q.id] || ""} onValueChange={(v) => onAnswer(q.id, v)}>
                      <SelectTrigger data-testid={`readiness-q-${q.id}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                      <SelectContent>{q.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : q.type === "textarea" ? (
                    <Textarea value={answers[q.id] || ""} onChange={(e) => onAnswer(q.id, e.target.value)} placeholder={q.placeholder} rows={2} data-testid={`readiness-q-${q.id}`} />
                  ) : (
                    <Input type={q.type === "number" ? "number" : "text"} value={answers[q.id] || ""} onChange={(e) => onAnswer(q.id, e.target.value)} placeholder={q.placeholder} data-testid={`readiness-q-${q.id}`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Readiness() {
  const [data, setData] = useState(null);
  const [checklist, setChecklist] = useState({});
  const [answers, setAnswers] = useState({});
  const [openKey, setOpenKey] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(() => {
    api.get("/readiness").then((r) => {
      setData(r.data);
      setChecklist(r.data.checklist || {});
      setAnswers(r.data.answers || {});
      setOpenKey(r.data.sections?.[0]?.key || null);
    }).catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const score = useMemo(() => {
    if (!data) return null;
    const sections = data.sections.map((s) => {
      const n = s.checklist.length + s.questions.length;
      let done = s.checklist.filter((it) => checklist[it.id]).length;
      done += s.questions.filter((q) => {
        const v = answers[q.id];
        return v !== undefined && v !== null && String(v).trim() !== "";
      }).length;
      return { key: s.key, done, total: n, pct: n ? Math.round((done / n) * 100) : 0 };
    });
    const total = sections.reduce((a, x) => a + x.total, 0);
    const done = sections.reduce((a, x) => a + x.done, 0);
    const overall = total ? Math.round((done / total) * 100) : 0;
    return { overall_pct: overall, done, total, band: overall >= 80 ? "ready" : overall >= 40 ? "in_progress" : "early", sections };
  }, [data, checklist, answers]);

  const setCheck = (id, v) => { setChecklist((p) => ({ ...p, [id]: v })); setDirty(true); };
  const setAnswer = (id, v) => { setAnswers((p) => ({ ...p, [id]: v })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/readiness", { checklist, answers });
      toast.success("Readiness saved");
      setDirty(false);
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const downloadPdf = async () => {
    try {
      if (dirty) await api.put("/readiness", { checklist, answers });
      const r = await api.get("/readiness/export.pdf", { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = u; a.download = "lestari_readiness_checklist.pdf"; a.click();
      URL.revokeObjectURL(u);
      setDirty(false);
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!data || !score) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading readiness guide…</div>;
  const band = BAND[score.band];

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-5xl" data-testid="readiness-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-1">Getting started · Onboarding readiness</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter flex items-center gap-3"><ClipboardList className="h-8 w-8 text-accent" /> Readiness Checklist & Questionnaire</h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-2xl">A practical guide for a Singapore SME to gather the people, data and evidence Lestari needs. Each section explains <span className="text-foreground">why it matters</span> and where it's used in the platform. Your answers are saved privately to your workspace.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={downloadPdf} data-testid="readiness-download-pdf"><Download className="h-4 w-4" /> Download PDF</Button>
          <Button variant="primary" onClick={save} disabled={saving} data-testid="readiness-save"><Save className="h-4 w-4" /> {saving ? "Saving…" : dirty ? "Save" : "Saved"}</Button>
        </div>
      </div>

      {/* score header */}
      <div className="card-flat p-5 flex items-center gap-6 flex-wrap" data-testid="readiness-score">
        <div>
          <div className="label-eyebrow text-[10px] mb-1 inline-flex items-center gap-1"><Sparkles className="h-3.5 w-3.5" /> Overall readiness</div>
          <div className={`font-mono text-5xl font-bold tracking-tighter ${band.color}`}>{score.overall_pct}<span className="text-base text-muted-foreground">%</span></div>
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center justify-between mb-1.5">
            <Badge variant={band.tone} className="text-[10px]" data-testid="readiness-band">{band.label}</Badge>
            <span className="text-xs text-muted-foreground font-mono">{score.done}/{score.total} items prepared</span>
          </div>
          <Progress value={score.overall_pct} className="h-2.5" />
          <p className="text-[11px] text-muted-foreground mt-2">Aim for 80%+ before activating reporting. You don't need everything perfect — start with profile, energy and data owners.</p>
        </div>
      </div>

      {/* sections */}
      <div className="space-y-3">
        {data.sections.map((s) => (
          <SectionCard
            key={s.key}
            s={s}
            checklist={checklist}
            answers={answers}
            onCheck={setCheck}
            onAnswer={setAnswer}
            open={openKey === s.key}
            onToggle={() => setOpenKey((k) => (k === s.key ? null : s.key))}
            sectionScore={score.sections.find((x) => x.key === s.key)}
          />
        ))}
      </div>
    </div>
  );
}
